import React from 'react';
import { Shield } from 'lucide-react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">iOS PreCheck</span>
            </div>
            <a href="/" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors">Back to Home</a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          
          <p className="text-gray-600 mb-8">Last Updated: January 2025</p>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Who We Are</h2>
            <p className="text-gray-700 mb-6">
              iOS PreCheck is operated by HECANO LLC. This Privacy Policy explains how we collect, use, and protect your information when you use our iOS app compliance checking service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Payment Information:</strong> Processed securely by Stripe. We never store your credit card details.</li>
              <li><strong>App Files:</strong> You upload .ipa files for analysis. These are temporarily stored for analysis only.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Usage Data:</strong> IP address, browser type, device information, and pages visited</li>
              <li><strong>Analytics:</strong> We use standard web analytics to understand how our service is used</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Process your payment and deliver compliance analysis reports</li>
              <li>Analyze your .ipa file for App Store compliance issues</li>
              <li>Improve our service and fix bugs</li>
              <li>Respond to your support requests</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Data Storage and Security</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>App Files:</strong> Your .ipa files are stored temporarily in memory during analysis and automatically deleted within 1 hour or immediately after analysis completes</li>
              <li><strong>Payment Data:</strong> All payment information is handled by Stripe. We never store credit card numbers</li>
              <li><strong>Security:</strong> We use industry-standard encryption (HTTPS/TLS) for all data transmission</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Data Sharing</h2>
            <p className="text-gray-700 mb-4">We do not sell, rent, or trade your personal information. We share data only with:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Stripe:</strong> For payment processing (see <a href="https://stripe.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>)</li>
              <li><strong>Anthropic:</strong> For AI-powered analysis (metadata only, not your binary code)</li>
              <li><strong>Legal Requirements:</strong> If required by law or to protect our rights</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Request a copy of data we have about you</li>
              <li>Request deletion of your data</li>
              <li>Opt out of marketing communications (if we send any)</li>
              <li>Lodge a complaint with a data protection authority</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Data Retention</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li><strong>Transaction Records:</strong> Kept for 7 years for tax and legal compliance</li>
              <li><strong>App Files:</strong> Deleted within 1 hour of upload</li>
              <li><strong>Analysis Results:</strong> Not stored on our servers after delivery to you</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Cookies</h2>
            <p className="text-gray-700 mb-4">We use essential cookies for:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Session management</li>
              <li>Security</li>
              <li>Analytics</li>
            </ul>
            <p className="text-gray-700 mb-6">
              You can disable cookies in your browser, but some features may not work properly.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 mb-6">
              Our service is not intended for users under 13. We do not knowingly collect information from children.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">GDPR Compliance (EU Users)</h2>
            <p className="text-gray-700 mb-4">If you're in the EU, you have additional rights under GDPR:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Right to access your data</li>
              <li>Right to rectification</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
            </ul>
            <p className="text-gray-700 mb-6">
              Our legal basis for processing: Contract performance (providing the service you paid for)
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">California Privacy Rights (CCPA)</h2>
            <p className="text-gray-700 mb-4">California residents have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-2">
              <li>Know what personal information is collected</li>
              <li>Know if personal information is sold or disclosed</li>
              <li>Opt out of sale of personal information (we don't sell data)</li>
              <li>Request deletion of personal information</li>
              <li>Non-discrimination for exercising privacy rights</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">For privacy questions or requests:</p>
            <ul className="list-none text-gray-700 mb-6 space-y-2">
              <li>Email: support@iosprecheck.com</li>
              <li>Company: HECANO LLC</li>
            </ul>

            <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date. Continued use of the service constitutes acceptance of changes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;